import { Component, computed, DestroyRef, effect, inject, signal, Signal, ViewChild } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { ClientService } from '@lib/client/client.service';
import type { Client, ClientAddress } from '@lib/api/models/client';
import { ComponentsModule, DialogService, SnackBarService, TabItem } from '@mastechnicaegroup/carelinelive-ui';
import { PaginatedApiResponse } from '@lib/api/responses/paginated-api-response';
import { BehaviorSubject, catchError, combineLatest, debounceTime, distinctUntilChanged, map, of, shareReplay, startWith, switchMap, take, tap, timer } from 'rxjs';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Result, toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { PaginationCardComponent } from '@shared/pagination/pagination-card.component';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { GoogleMap } from '@angular/google-maps';
import { ApiResponse } from '@lib/api/responses/api-response';
import { getDisplayName } from '@lib/api/models/person';
import { HeaderComponent } from '@shared/layout/header/header.component';
import { PersonNamePipe } from '@shared/pipes/person.pipes';
import { MarkerClusterer, Renderer } from '@googlemaps/markerclusterer';
import { APP_CONSTANTS, DEFAULT_MAP_ID, DEFAULT_MAP_MAXZOOM, DEFAULT_MAP_MINZOOM, DEFAULT_MAP_ZOOM, posToLatLngLiteral } from '@lib/constants';
import type { Cluster } from '@googlemaps/markerclusterer/dist/cluster';
import { markerSvg } from '../../../../assets/markers.svg';
import { polarProjectLatLng } from '@lib/api/helpers';
import { DEFAULT_MAP_CENTER } from '@lib/constants';
import AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement;
import { PromptClientStatusChangeDialogComponent } from '../../dialogs/client/prompt/prompt-client-status-change-dialog.component';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { AuthenticationService } from '@lib/auth/authentication.service';
import { postParamNamesFilterActive, postParamNamesFilterQuery } from '@shared/filters/filter-params';

@Component({
    selector: 'app-clients-index-page',
    templateUrl: './clients-index-page.component.html',
    styleUrls: ['./clients-index-page.component.scss'],
    standalone: true,
    imports: [
        ComponentsModule,
        AsyncPipe,
        PaginationCardComponent,
        RouterLink,
        ReactiveFormsModule,
        GoogleMap,
        HeaderComponent,
        PersonNamePipe,
        ErrorPanelComponent
    ]
})
export class ClientsIndexPageComponent {
    @ViewChild(GoogleMap) googleMapView!: GoogleMap;

    private readonly _activeRoute = inject(ActivatedRoute);
    private readonly _clientService = inject(ClientService);
    private readonly _router = inject(Router);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _snackBar = inject(SnackBarService);
    private readonly _dialog = inject(DialogService);
    private readonly _authService = inject(AuthenticationService);

    protected currentUser = toSignal(this._authService.user$);

    private markerLibrary: google.maps.MarkerLibrary | undefined;
    private googleMap: google.maps.Map | undefined;
    private readonly intersectionObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
            if (entry.isIntersecting) {
                entry.target.classList.add('drop');
                this.intersectionObserver.unobserve(entry.target);
            }
        }
    });

    private readonly refreshTrigger$ = new BehaviorSubject<void>(undefined);

    protected readonly getDisplayName = getDisplayName;

    protected currentTabId$ = this._activeRoute.queryParams.pipe(
        map((params): string => params['tab'] ?? '0'),
        distinctUntilChanged(),
        shareReplay(1)
    );

    /** The type of view list or map */
    protected listType = toSignal<'list' | 'map'>(this._activeRoute.queryParams.pipe(
        map((params) => params['type'] === 'map' ? 'map' : 'list')
    ));

    protected readonly mapError = signal<ErrorEvent | null>(null);

    /** The search form control */
    protected readonly searchControl = new FormControl<string>(
        this._activeRoute.snapshot.queryParams['q'] ?? '',
        [Validators.maxLength(50)]
    );

    /** An observable that emits the search query */
    protected readonly searchQuery = toSignal(this.searchControl.valueChanges.pipe(
        startWith(this.searchControl.value),

        debounceTime(APP_CONSTANTS.debounceTimeMs),

        // If it's an empty string, return null
        map(value => !value ? undefined : value)
    ));

    /** The current page being shown */
    protected readonly page = toSignal<number>(this._activeRoute.queryParams.pipe(
        map((params): number => params['page'] ?? 1)
    ));

    /** Fetch the page of clients */
    protected readonly pageResult: Signal<Result<PaginatedApiResponse<Client>> | undefined> = toSignal(combineLatest({
        page: toObservable(this.page),
        searchQuery: toObservable(this.searchQuery),
        tabId: this.currentTabId$,
        refresh: this.refreshTrigger$.asObservable()
    }).pipe(
        switchMap(({ page, searchQuery, tabId }) => {
            // if we have selected clients remove them if they are no longer
            // in the query
            this.selectedClientIds.update(() => []);
            return this._clientService.list(page, {
                [postParamNamesFilterQuery]: searchQuery,
                [postParamNamesFilterActive]: tabId === '0' ? '' : tabId === '1' ? 'true' : 'false'
            }).pipe(
                // Turn into a Result
                toResult()
            );
        }),

        // Share the same result with all new subscribers
        shareReplay(1)
    ));

    protected readonly pagePinsResult: Signal<Result<ApiResponse<ClientAddress[]>> | undefined> = toSignal(combineLatest({
        searchQuery: toObservable(this.searchQuery)
    }).pipe(
        switchMap(({ searchQuery }) => this._clientService.getAddresses({
            [postParamNamesFilterQuery]: searchQuery
        }).pipe(
            // Turn into a Result
            toResult()
        )),

        // Share the same result with all new subscribers
        shareReplay(1)
    ));

    protected markers = computed(() => {
        const clientsAddresses = this.pagePinsResult()?.value?.data;
        const computedMarkers: {
            addressId: string,
            title: string,
            lat: number,
            lng: number,
            clientId: string
        }[] = [];
        if (!clientsAddresses) {
            return [];
        }
        clientsAddresses.forEach((clientAddress) => {
            if (clientAddress.person.address.lng !== null && clientAddress.person.address.lat !== null) {
                computedMarkers.push({
                    addressId: clientAddress.person.address.id!,
                    title: getDisplayName(clientAddress.person),
                    lat: +clientAddress.person.address.lat!,
                    lng: +clientAddress.person.address.lng!,
                    clientId: clientAddress.id
                });
            }
        });
        return computedMarkers;
    });

    protected readonly markers$ = toObservable(this.markers);

    protected markerPins = signal<google.maps.marker.AdvancedMarkerElement[]>([]);

    private activeMarker: google.maps.marker.AdvancedMarkerElement | undefined;

    private markerRenderer: Renderer = {

        render: ({ count, position }) => {
            const advancedMarker = new google.maps.marker.AdvancedMarkerElement({
                position,
                content: markerSvg.makeDomSvg('client', count.toString(), 'A cluster of clients'),
                // adjust zIndex to be above other markers
                zIndex: 1000000 - Math.floor(position.lat() * 10000),
                title: 'A cluster of clients'
            });
            this.dropMarker(advancedMarker);
            return advancedMarker;
        }
    };

    private clusterRenderer: MarkerClusterer | null = null;

    protected readonly selectedClientIds = signal<readonly string[] | null>(null);
    protected readonly selectedClientId = signal<string | null>(null);
    protected readonly selectedClient = computed(() => {
        return this.pagePinsResult()?.value?.data.find(client => this.selectedClientId() === client.id);
    });

    protected readonly tabItems: TabItem[] = [
        {
            disabled: false,
            id: '0',
            title: 'All'
        },
        {
            disabled: false,
            id: '1',
            title: 'Active'
        },
        {
            disabled: false,
            id: '2',
            title: 'Inactive'
        }
    ];

    protected readonly clientTabItems = computed(() => {
        const clientIds = this.selectedClientIds() || [];
        if (this.pagePinsResult()?.value?.data === undefined) {
            return [];
        }
        return clientIds.map((clientId) => {
            const tabClient = this.pagePinsResult()!.value?.data.find(client => clientId === client.id);
            return {
                disabled: false,
                id: clientId,
                title: tabClient!.person.initials
            };
        });
    });

    /** The pagination meta data */
    protected readonly meta = computed(() => {
        // Extract the meta data from the page result
        return this.pageResult()?.value?.meta;
    });

    protected options: google.maps.MapOptions = {
        center: posToLatLngLiteral(this._activeRoute.snapshot.queryParams['pos']) ?? DEFAULT_MAP_CENTER,
        zoom: this._activeRoute.snapshot.queryParams['zoom']
            ? parseFloat(this._activeRoute.snapshot.queryParams['zoom'])
            : DEFAULT_MAP_ZOOM,
        minZoom: DEFAULT_MAP_MINZOOM,
        maxZoom: DEFAULT_MAP_MAXZOOM,
        zoomControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: false,
        clickableIcons: false,
        mapId: DEFAULT_MAP_ID
    };

    private markerListeners: google.maps.MapsEventListener[] = [];

    constructor() {
        // Update the query params when the page or search query changes
        effect(() => {
            this._router.navigate([], {
                relativeTo: this._activeRoute,
                queryParams: {
                    q: this.searchQuery()
                },
                queryParamsHandling: 'merge',

                // Don't push a new history state, just replace the query param
                replaceUrl: true
            });
        });
        this._destroyRef.onDestroy(() => {
            this.intersectionObserver.disconnect();
        });
    }

    async currentTabIdChange($event: string) {
        await this._router.navigate([], {
            queryParams: { tab: $event },
            queryParamsHandling: 'merge'
        });
    }

    async initMap(googleMap: google.maps.Map) {
        this.googleMap = googleMap;

        if (this.markerLibrary === undefined) {
            this.markerLibrary = await google.maps.importLibrary('marker') as google.maps.MarkerLibrary;
        }

        this.markers$.pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(() => {

            const clientAddresses = this.pagePinsResult()?.value?.data;
            if (!clientAddresses) {
                return;
            }

            const bounds = new google.maps.LatLngBounds();

            // some cleanup
            this.markerCleanup();

            const newPins: google.maps.marker.AdvancedMarkerElement[] = [];
            clientAddresses.forEach(clientAddress => {
                const pos = new google.maps.LatLng(+clientAddress.person.address.lat, +clientAddress.person.address.lng);
                bounds.extend(pos);
                const label = clientAddress.person.initials;
                const pin = new google.maps.marker.AdvancedMarkerElement({
                    map: this.googleMap,
                    content: markerSvg.makeDomSvg('client', label, getDisplayName(clientAddress.person)),
                    position: pos,
                    zIndex: 1000000 - Math.floor(pos.lat() * 10000),
                    title: getDisplayName(clientAddress.person)
                });
                pin.setAttribute('clientId', clientAddress.id);
                this.dropMarker(pin);
                const listener = pin.addListener('click', () => {
                    this.setActive(pin);
                    this.openClientInfo([clientAddress.id]);
                });
                this.markerListeners.push(listener);
                newPins.push(pin);
            });
            this.markerPins.set(newPins);

            // only if we have markers - fit the bounds
            if (newPins.length) {
                // ALWAYS ensure there is some padding around the markers
                const center = bounds.getCenter();
                const point1 = polarProjectLatLng(center!.lat(), center!.lng(), 45, 100);
                const point2 = polarProjectLatLng(center!.lat(), center!.lng(), 225, 100);
                bounds.extend(new google.maps.LatLng(...point1));
                bounds.extend(new google.maps.LatLng(...point2));
                if (!this._activeRoute.snapshot.queryParams['pos']) {
                    googleMap.fitBounds(bounds);
                }
            }


            // Add a marker clusterer to manage the markers.
            this.clusterRenderer = new MarkerClusterer({
                markers: this.markerPins(),
                map: this.googleMap,
                renderer: this.markerRenderer,
                onClusterClick: (event: google.maps.MapMouseEvent, cluster: Cluster) => {
                    if (cluster.marker instanceof google.maps.marker.AdvancedMarkerElement) {
                        this.setActive(cluster.marker);
                    }
                    this.openClientCluster(cluster);
                },
                algorithmOptions: {
                    maxZoom: 20
                }
            });
            // if we're only showing 1 pin, open the info
            if (newPins.length === 1) {
                const delayedSourceClientId$ = timer(500).pipe(map(() => clientAddresses[0].id));
                delayedSourceClientId$.pipe(take(1)).subscribe((clientId) => {
                    this.openClientInfo([clientId]);
                    this.dropMarker(newPins[0]);
                    this.activeMarker = newPins[0];
                });
            }

            // if no pins, zoom out
            if (newPins.length === 0 && this.googleMap !== undefined && !this._activeRoute.snapshot.queryParams['zoom']) {
                googleMap.setZoom(DEFAULT_MAP_ZOOM);
            }
        });
    }

    protected nextPage(): void {
        const meta = this.meta();

        if (meta) {
            this.setPage(meta.current_page + 1);
        }
    }

    protected previousPage(): void {
        const meta = this.meta();

        if (meta) {
            this.setPage(meta.current_page - 1);
        }
    }

    protected async setPage(page: number) {
        await this._router.navigate([], {
            queryParams: { page },
            queryParamsHandling: 'merge'
        });
    }

    updateListType(list: 'map' | 'list') {
        this.openClientInfo([]);
        this._router.navigate([],
            {
                relativeTo: this._activeRoute,
                queryParams: { 'type': list },
                queryParamsHandling: 'merge' // remove to replace all query params by provided
            });
    }

    openClientInfo(selectedClientIds: (string | null)[]) {
        this.selectedClientIds.update(oldClientIds => {
            const newClientIds: string[] = []; // all deselected
            selectedClientIds.forEach((clientId) => {
                if (clientId !== null) {
                    if (oldClientIds === null || !oldClientIds.includes(clientId)) {
                        newClientIds.push(clientId); // select if not in current selection
                    }
                }
            });
            return newClientIds;
        });
        if (this.selectedClientIds()!.length > 0) {
            // opened tab should be the first one
            this.selectedClientId.update(() => this.selectedClientIds()![0]);
        } else {
            // nothing selected
            this.selectedClientId.update(() => null);
        }
    }

    onZoomChange() {
        // clear the selected clients away as changing zoom will change the clusters
        this.selectedClientIds.update(() => []);
        this.selectedClientId.update(() => null);
        this.setActive(null);
        this.mapPosToQueryParams();
    }

    openClientCluster(cluster: Cluster) {
        if (!cluster.markers) {
            return;
        }
        const clientIds = cluster.markers.map(marker => (marker as AdvancedMarkerElement).getAttribute('clientId'));
        this.openClientInfo(Array.from(clientIds));
    }

    editSelectedClient() {
        const clientId = this.selectedClientId();
        if (clientId) {
            this._router.navigate(['/clients/', clientId]);
        }
    }

    navigateToAddClient() {
        this._router.navigate(['/add-client']);
    }

    currentClientTabIdChange($event: string) {
        this.selectedClientId.update(() => $event);
    }

    handleMapError($event: ErrorEvent) {
        this.mapError.set($event);
    }

    markerCleanup(): void {
        this.markerPins().forEach((marker) => {
            marker.remove();
            marker.map = null;
        });
        this.clusterRenderer?.clearMarkers();
        this.markerPins.set([]);
        this.markerListeners.forEach((listener) => {
            google.maps.event.removeListener(listener);
        });
    }

    getFormattedAddress(client: Client | ClientAddress | undefined): string {
        const address = client?.person?.address;
        if (!address?.street_1 && !address?.post_code) {
            return '';
        }

        const parts = [
            address.street_1 ?? 'No street address',
            address.post_code
        ].filter(Boolean);

        return parts.join(', ');
    }

    private setActive(pin: google.maps.marker.AdvancedMarkerElement | null) {

        if (this.activeMarker !== undefined) {
            const content = this.activeMarker.content as HTMLElement;
            content.classList.add('shrink');
            content.classList.remove('grow');
            if (pin === this.activeMarker) {
                this.activeMarker = undefined;
                return;
            }
            this.activeMarker = undefined;
        }
        if (pin) {
            if (this.activeMarker !== pin) {
                const content = pin.content as HTMLElement;
                this.activeMarker = pin;
                content.classList.add('grow');
                content.classList.remove('shrink');
            }
        }
    }

    private dropMarker(advanceMarker: google.maps.marker.AdvancedMarkerElement) {
        const content = advanceMarker.content as HTMLElement;
        content.style.opacity = '0';
        content.addEventListener('animationend', () => {
            content.classList.remove('drop');
            content.style.opacity = '1';
        }, {
            once: true
        });
        const time = Math.random();
        content.style.setProperty('--delay-time', time + 's');
        this.intersectionObserver.observe(content);
    }

    onDrag() {
        this.mapPosToQueryParams();
    }

    private mapPosToQueryParams() {
        this.options.center = this.googleMap?.getCenter() ?? DEFAULT_MAP_CENTER;
        this._router.navigate([],
            {
                relativeTo: this._activeRoute,
                queryParams: {
                    'pos': this.googleMap?.getCenter()?.lat() + ',' + this.googleMap?.getCenter()?.lng(),
                    'zoom': this.googleMap?.getZoom()
                },
                queryParamsHandling: 'merge'
            });
    }

    changeStatus(client: Client, status: boolean) {

        const clientData = {
            ...client,
            status: status
        };

        return PromptClientStatusChangeDialogComponent.show(this._dialog, clientData).closed.pipe(
            tap(() => {
                this.refreshTrigger$.next();
            }),
            catchError(() => {
                this._snackBar.open({
                    colour: 'error',
                    dismissable: false,
                    icon: 'error_outline',
                    title: 'Client status failed to change',
                    actions: [
                        { id: 'undo', label: 'OK' }
                    ]
                });
                return of(null);
            })
        ).subscribe();
    }
}
